const EMPTY_STATE = {
  pdfAnalytics: [],
  overviewStats: {
    totalViews: '0',
    uniqueVisitors: '0',
    avgSessionTime: 0,
    totalPages: 0
  },
  pageTimeData: [],
  sources: [],
  linkAnalytics: []
}

export const extractSources = (apiData) =>
  apiData.map((entry) => ({
    value: entry.label,
    label: entry.label
  }))

export const filterDataBySource = (apiData, selectedSource) =>
  selectedSource === 'all' ? apiData : apiData.filter((entry) => entry.label === selectedSource)

export const hasValidPageData = (dataToProcess) =>
  dataToProcess.length > 0 && dataToProcess.some((entry) => entry.pageData && entry.pageData.length > 0)

export const aggregatePageData = (dataToProcess) => {
  const aggregatedPageData = {}

  dataToProcess.forEach((entry) => {
    if (entry.pageData && entry.pageData.length > 0) {
      entry.pageData.forEach((page) => {
        const pageNum = parseInt(page.page)
        if (!aggregatedPageData[pageNum]) {
          aggregatedPageData[pageNum] = {
            visitors: 0,
            totalTime: 0
          }
        }
        aggregatedPageData[pageNum].visitors += page.visitors || 0
        aggregatedPageData[pageNum].totalTime += page.totalTime || 0
      })
    }
  })

  return Object.keys(aggregatedPageData)
    .map((pageNum) => ({
      page: parseInt(pageNum),
      data: aggregatedPageData[pageNum]
    }))
    .sort((a, b) => a.page - b.page)
}

export const calculatePageMetrics = (sortedPages) => {
  const firstPageVisitors = sortedPages.length > 0 ? sortedPages[0].data.visitors : 0

  return sortedPages.map((pageInfo, index) => {
    const { page, data } = pageInfo

    let bounceRate = 0
    if (index < sortedPages.length - 1) {
      const nextPageVisitors = sortedPages[index + 1].data.visitors
      bounceRate = Math.round(((data.visitors - nextPageVisitors) / data.visitors) * 100)
    }

    const retentionRate = firstPageVisitors > 0 ? (data.visitors / firstPageVisitors) * 100 : 0
    const positionFactor = 1 - (index / sortedPages.length) * 0.3
    const engagement = Math.round(retentionRate * positionFactor)

    const avgTime = data.totalTime && data.visitors > 0 ? Math.round(data.totalTime / data.visitors) : 0

    return {
      page,
      pageviews: Math.round(data.visitors * 1.2),
      visitors: data.visitors,
      avgTime,
      bounceRate: Math.max(0, Math.min(100, bounceRate)),
      engagement: Math.max(0, Math.min(100, engagement))
    }
  })
}

export const aggregateLinkData = (dataToProcess) => {
  const aggregatedLinkData = {}

  dataToProcess.forEach((entry) => {
    if (entry.linkData && entry.linkData.length > 0) {
      entry.linkData.forEach((link) => {
        if (!aggregatedLinkData[link.url]) {
          aggregatedLinkData[link.url] = { url: link.url, clicks: 0 }
        }
        aggregatedLinkData[link.url].clicks += link.clicks || 0
      })
    }
  })

  return Object.values(aggregatedLinkData).sort((a, b) => b.clicks - a.clicks)
}

export const calculateOverviewStats = (pdfAnalytics, sortedPages) => {
  const calculatedTotalViews = pdfAnalytics.reduce((sum, page) => sum + page.pageviews, 0)
  const calculatedUniqueVisitors = sortedPages.length > 0 ? sortedPages[0].data.visitors : 0

  const avgSessionTime =
    pdfAnalytics.length > 0
      ? Math.round(pdfAnalytics.reduce((sum, page) => sum + page.avgTime, 0) / pdfAnalytics.length)
      : 0

  return {
    totalViews: calculatedTotalViews.toLocaleString(),
    uniqueVisitors: calculatedUniqueVisitors.toLocaleString(),
    avgSessionTime,
    totalPages: sortedPages.length
  }
}

export const transformAPIData = (apiData, selectedSource = 'all') => {
  if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
    return EMPTY_STATE
  }

  const sources = extractSources(apiData)
  const dataToProcess = filterDataBySource(apiData, selectedSource)
  const linkAnalytics = aggregateLinkData(dataToProcess)

  if (!hasValidPageData(dataToProcess)) {
    return { ...EMPTY_STATE, sources, linkAnalytics }
  }

  const sortedPages = aggregatePageData(dataToProcess)
  const pdfAnalytics = calculatePageMetrics(sortedPages)
  const overviewStats = calculateOverviewStats(pdfAnalytics, sortedPages)

  const pageTimeData = pdfAnalytics.map((page) => ({
    page: page.page,
    avgTime: page.avgTime
  }))

  return {
    pdfAnalytics,
    overviewStats,
    pageTimeData,
    sources,
    linkAnalytics
  }
}
